import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTeam } from '../../contexts/TeamContext';
import { useAuth } from '../../contexts/AuthContext';
import { CreateTeamDialog } from '../../components/teams/CreateTeamDialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Users, Plus, Building2, Mail } from 'lucide-react';

export function TeamOnboardingPage() {
  const navigate = useNavigate();
  const { teams, refreshTeams } = useTeam();
  const { user } = useAuth();
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // If user already has teams, redirect to dashboard. In an effect, not in
  // render: navigating during render is a side effect on another component
  // and React warns about it under StrictMode.
  useEffect(() => {
    if (teams.length > 0) {
      navigate('/');
    }
  }, [teams.length, navigate]);

  if (teams.length > 0) {
    return null;
  }

  const handleTeamCreated = async () => {
    await refreshTeams();
    navigate('/');
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-primary/10 flex items-center justify-center p-4"
      dir="rtl"
    >
      <div className="w-full max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-2">
            ברוכים הבאים ל-RadiaQ AI
          </h1>
          <p className="text-lg text-muted-foreground">
            כדי להתחיל, צור צוות חדש — או בקש ממנהל צוות קיים לצרף אותך
          </p>
        </div>

        {/* Options */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Create Team Card */}
          <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-primary/50">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <Plus className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-xl">צור צוות חדש</CardTitle>
              </div>
              <CardDescription className="text-base">
                התחל צוות חדש והזמן חברי צוות להצטרף אליך
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 mb-6 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  <span>אתה תהיה מנהל הצוות</span>
                </li>
                <li className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  <span>הזמן חברי צוות ללא הגבלה</span>
                </li>
                <li className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  <span>התחל עם מרחב עבודה ריק</span>
                </li>
              </ul>
              <CreateTeamDialog
                open={showCreateDialog}
                onOpenChange={setShowCreateDialog}
                onTeamCreated={handleTeamCreated}
              >
                <Button className="w-full" size="lg">
                  <Plus className="ml-2 h-5 w-5" />
                  צור צוות חדש
                </Button>
              </CreateTeamDialog>
            </CardContent>
          </Card>

          {/*
            Joining a team is not a self-service action (#121). Until an admin
            adds this address there is nothing for the user to press, so this
            card tells them what to ask for and shows the address the admin has
            to type — rather than offering a button that cannot work.
          */}
          <Card className="border-2 border-dashed">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-3 bg-muted rounded-lg">
                  <Users className="h-6 w-6 text-muted-foreground" />
                </div>
                <CardTitle className="text-xl">הצטרף לצוות קיים</CardTitle>
              </div>
              <CardDescription className="text-base">
                מנהל הצוות מצרף אותך לפי כתובת האימייל שלך. אין הצטרפות עצמית.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 mb-6 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <span>שלח למנהל הצוות את הכתובת שלמטה</span>
                </li>
                <li className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  <span>הוא מוסיף אותך במסך ניהול חברי הצוות</span>
                </li>
                <li className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  <span>רענן את הדף והצוות יופיע</span>
                </li>
              </ul>
              <div className="rounded-md border bg-muted/40 px-3 py-2 text-center">
                <p className="text-xs text-muted-foreground mb-1">
                  הכתובת שלך במערכת
                </p>
                <p className="font-mono text-sm break-all" dir="ltr">
                  {user?.email ?? '—'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground">
            תוכל תמיד ליצור צוותים נוספים מההגדרות
          </p>
        </div>
      </div>
    </div>
  );
}
